import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  Container,
  Chip,
  CardActionArea,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserSecret,
  faUserTie,
  faChalkboardTeacher,
  faUsers,
  faRoute,
} from "@fortawesome/free-solid-svg-icons";
import TpAddUserModal from "./TpAddUserModal";
import TpUserTypes from "./TpUserTypes";

const TpUsers = () => {
  const [userCounts, setUserCounts] = useState({
    admin: 0,
    coordinator: 0,
    instructor: 0,
    staff: 0,
    pathwayManager: 0,
  });
  const [loading, setLoading] = useState(true);
  const [userTypesModalOpen, setUserTypesModalOpen] = useState(false);
  const navigate = useNavigate();

  const fetchUserCounts = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const counts = {
        admin: users.filter((user) => {
          // Include both active and inactive admin users in the total admin count
          const hasAdminType = user.userTypes?.admin === true;
          const hasLegacyFlag = user.techPrepAdminFlag === true;
          return hasAdminType || hasLegacyFlag;
        }).length,
        coordinator: users.filter((user) => user.userTypes?.coordinator).length,
        instructor: users.filter((user) => user.userTypes?.instructor).length,
        staff: users.filter((user) => user.userTypes?.staff).length,
        pathwayManager: users.filter((user) => user.userTypes?.pathwayManager)
          .length,
      };

      // Calculate breakdown for admin users
      const adminUsers = users.filter((user) => {
        const hasAdminType = user.userTypes?.admin === true;
        const hasLegacyFlag = user.techPrepAdminFlag === true;
        return hasAdminType || hasLegacyFlag;
      });

      const activeAdmins = adminUsers.filter((user) => {
        const hasLegacyFlag = user.techPrepAdminFlag === true;
        const hasAdminType = user.userTypes?.admin === true;
        const allUserTypesFalse =
          user.userTypes &&
          Object.values(user.userTypes).every(
            (value) => value === false || value === 0
          );
        const noUserTypes =
          !user.userTypes || Object.keys(user.userTypes).length === 0;
        const isInactive =
          hasLegacyFlag && !hasAdminType && (allUserTypesFalse || noUserTypes);
        return !isInactive;
      }).length;

      const inactiveAdmins = adminUsers.length - activeAdmins;

      counts.adminBreakdown = {
        active: activeAdmins,
        inactive: inactiveAdmins,
      };

      setUserCounts(counts);
    } catch (error) {
      console.error("Error fetching user counts: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserCounts();
  }, []);

  const userTypeCards = [
    {
      type: "admin",
      title: "Admins",
      icon: faUserSecret,
      color: "#f44336",
      route: "/TpAdmins",
    },
    {
      type: "coordinator",
      title: "Coordinators",
      icon: faUserTie,
      color: "#2196f3",
      route: "/TpCoordinators",
    },
    {
      type: "instructor",
      title: "Instructors",
      icon: faChalkboardTeacher,
      color: "#4caf50",
      route: "/TpInstructors",
    },
    {
      type: "staff",
      title: "Staff",
      icon: faUsers,
      color: "#ff9800",
      route: "/TpStaff",
    },
    {
      type: "pathwayManager",
      title: "Pathway Managers",
      icon: faRoute,
      color: "#9c27b0",
      route: "/TpPathwayManagers",
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 15 }}>
      <Box display="flex" alignItems="center" mb={4}>
        <Typography
          variant="h4"
          component="div"
          gutterBottom
          sx={{ flexGrow: 1 }}
        >
          Tech Prep Users Dashboard
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <TpAddUserModal onSuccess={fetchUserCounts} />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setUserTypesModalOpen(true)}
          >
            Usertypes
          </Button>
        </Box>
        <TpUserTypes
          open={userTypesModalOpen}
          onClose={() => setUserTypesModalOpen(false)}
        />
      </Box>

      <Typography variant="h6" color="text.secondary" mb={3}>
        Manage users by their role and permissions
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <Typography>Loading user counts...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3} style={{ marginBottom: "50px" }}>
          {userTypeCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.type}>
              <Card sx={{ height: "100%" }}>
                <CardActionArea
                  onClick={() => navigate(card.route)}
                  sx={{ height: "100%" }}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <FontAwesomeIcon
                      icon={card.icon}
                      style={{
                        color: card.color,
                        fontSize: "3rem",
                        marginBottom: "16px",
                      }}
                    />
                    <Typography variant="h5" component="div" gutterBottom>
                      {card.title}
                    </Typography>
                    <Chip
                      label={`${userCounts[card.type]} user${
                        userCounts[card.type] !== 1 ? "s" : ""
                      }`}
                      color="primary"
                      size="large"
                    />
                    <Typography variant="body2" color="text.secondary" mt={2}>
                      Click to view and manage {card.title.toLowerCase()}
                      {card.type === "admin" && userCounts.adminBreakdown && (
                        <>
                          <br />
                          Active: {userCounts.adminBreakdown.active} | Inactive:{" "}
                          {userCounts.adminBreakdown.inactive}
                        </>
                      )}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default TpUsers;
